/**
 * Single-contract payoff detail — Robinhood's "Simulated Returns" view.
 *
 * The chain tells you which contract to pick; this view answers "if I buy
 * (or sell) this one, what does my P&L look like against the underlying?".
 * The scrubber picks a target underlying price and the stat strip above it
 * recomputes P&L and contract value instantly — the two numbers that drive
 * the decision are always visible together.
 *
 * Payoff is intrinsic at expiration (max(S-K,0) for a call, max(K-S,0) for
 * a put) minus the premium. No time-interpolation yet; that needs an
 * options pricing model and is a follow-up.
 */
import type { OptionContract } from "@tt/shared";
import { formatNum } from "@tt/shared";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";

import { bsPrice } from "../lib/blackScholes";

type Action = "buy" | "sell";

export function OptionDetailModal({
  symbol, contract, kind, spot, expiry, days, onClose,
}: {
  symbol: string;
  contract: OptionContract;
  kind: "call" | "put";
  spot: number;
  expiry: string;
  days: number;
  onClose: () => void;
}) {
  const [action, setAction] = useState<Action>("buy");
  // Scrubbed underlying price — defaults to spot so the chart opens on a
  // neutral position; the user drags it to explore "what if the underlying
  // moves to X at expiration".
  const [target, setTarget] = useState<number>(spot);
  // 0 = today (full extrinsic value remaining), 1 = expiration (intrinsic
  // only, kinked line). Everything in between is Black-Scholes with the
  // contract's implied vol — same shape Robinhood's "Simulated Returns"
  // scrubber shows.
  const [timeProgress, setTimeProgress] = useState<number>(1);
  // Reset the scrubber when the contract (or spot) changes so an opening
  // between two different strikes doesn't inherit the old pin.
  useEffect(() => { setTarget(spot); setTimeProgress(1); }, [contract.strike, spot]);

  // Close on Escape, like every other modal here.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  // Prefer the ask when buying and the bid when selling — those are the
  // prices actually transactable right now. Fall back to the opposite side
  // if the preferred side is zero (common on illiquid contracts).
  const premium = action === "buy"
    ? (contract.ask || contract.bid || 0)
    : (contract.bid || contract.ask || 0);

  // Years-to-expiry at the scrubbed point in time. timeProgress 0 = today
  // (so T = days/365 remaining), 1 = expiration (T = 0, intrinsic only).
  const tYears = Math.max(0, (days / 365) * (1 - timeProgress));
  // Implied vol lives on the contract when the provider supplies it; fall
  // back to a neutral 30% when they don't (common on illiquid names) so
  // the chart still renders a smooth curve instead of collapsing to the
  // kink and silently misleading the user.
  const sigma = contract.iv !== null && contract.iv > 0 ? contract.iv : 0.30;

  // Per-contract P&L at a given underlying price. One contract = 100 shares.
  // At expiration (T = 0) this collapses to intrinsic payoff − premium; at
  // earlier times it's the Black-Scholes theoretical minus what you paid,
  // so the curve bows up toward the premium line the further out you are.
  const pnlAt = (s: number): number => {
    const theoretical = bsPrice({
      spot: s, strike: contract.strike, tYears, sigma, kind,
    });
    const direction = action === "buy" ? 1 : -1;
    return direction * (theoretical - premium) * 100;
  };

  const breakeven = kind === "call"
    ? contract.strike + premium
    : contract.strike - premium;

  // X-axis range: strike ± span. Picked wide enough to show the full kinked
  // line and a bit of slope beyond breakeven -- a user who wants more range
  // just drags the scrubber further; the whole chart redraws.
  const spanBase = Math.max(spot * 0.35, premium * 4, 2);
  const lo = Math.max(0.01, contract.strike - spanBase);
  const hi = contract.strike + spanBase;

  // Sample the payoff curve. 2 points at the kink are enough but we densify
  // around the strike so the fill-gradient boundary looks crisp on retina.
  const samples = useMemo(() => {
    const N = 200;
    const pts: { s: number; y: number }[] = [];
    for (let i = 0; i < N; i++) {
      const s = lo + ((hi - lo) * i) / (N - 1);
      pts.push({ s, y: pnlAt(s) });
    }
    return pts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, contract.strike, premium, lo, hi, tYears, sigma]);

  const W = 640;
  const H = 260;
  const PAD_X = 32;
  const PAD_Y = 20;
  const yVals = samples.map((p) => p.y);
  const yMin = Math.min(...yVals, 0);
  const yMax = Math.max(...yVals, 0);
  const ySpan = Math.max(1, (yMax - yMin));
  const xAt = (s: number) => PAD_X + ((s - lo) / (hi - lo)) * (W - PAD_X * 2);
  const yAt = (v: number) => PAD_Y + (1 - (v - yMin) / ySpan) * (H - PAD_Y * 2);
  const y0 = yAt(0);

  // Build the SVG path for the payoff line.
  const line = samples.map((p, i) =>
    `${i === 0 ? "M" : "L"}${xAt(p.s).toFixed(2)},${yAt(p.y).toFixed(2)}`
  ).join(" ");

  // Fill regions: above-zero fills green, below-zero fills red. We clip
  // the payoff polygon against y=0 so each colour only paints its half.
  const polyAbove = [
    `M${xAt(lo).toFixed(2)},${y0.toFixed(2)}`,
    ...samples.map((p) => `L${xAt(p.s).toFixed(2)},${yAt(Math.max(p.y, 0)).toFixed(2)}`),
    `L${xAt(hi).toFixed(2)},${y0.toFixed(2)}`,
    "Z",
  ].join(" ");
  const polyBelow = [
    `M${xAt(lo).toFixed(2)},${y0.toFixed(2)}`,
    ...samples.map((p) => `L${xAt(p.s).toFixed(2)},${yAt(Math.min(p.y, 0)).toFixed(2)}`),
    `L${xAt(hi).toFixed(2)},${y0.toFixed(2)}`,
    "Z",
  ].join(" ");

  const targetPnl = pnlAt(target);
  const targetPct = premium > 0 ? (targetPnl / (premium * 100)) * 100 : 0;

  // Clamp the scrubber inside the chart viewport while giving the slider
  // reasonable step precision — one cent is sharper than people's eyes but
  // cheap to compute.
  const scrubMin = Math.max(0.01, lo).toFixed(2);
  const scrubMax = hi.toFixed(2);

  // Format the title like Robinhood: "Buy AAPL $180 Call 1/15/27".
  const strikeLabel = `$${formatNum(contract.strike, contract.strike >= 10 ? 0 : 1)}`;
  const kindLabel = kind === "call" ? "Call" : "Put";
  const title = `${action === "buy" ? "Buy" : "Sell"} ${symbol} ${strikeLabel} ${kindLabel} ${formatExpiry(expiry)}`;

  // Decide colour for the headline P&L — matches Robinhood's up/down tint.
  const pnlTone = targetPnl > 0 ? "up" : targetPnl < 0 ? "down" : "";

  return (
    <AnimatePresence>
      <motion.div
        className="opt-detail-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="opt-detail"
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="opt-detail-head">
            <button className="opt-detail-close" onClick={onClose} aria-label="Close">×</button>
            <div className="opt-detail-title">{title}</div>
          </div>

          {/* Buy / sell segmented control — flipping it rebuilds the curve
              because selling a call pays you the premium up front, caps
              profit at the premium and takes unlimited risk above the
              strike. One rendering, two very different pictures. */}
          <div className="opt-detail-tabs">
            {(["buy", "sell"] as const).map((a) => (
              <button
                key={a}
                className={`opt-detail-tab ${action === a ? "opt-detail-tab-on" : ""}`}
                onClick={() => setAction(a)}
              >{a === "buy" ? "Buy" : "Sell"}</button>
            ))}
          </div>

          <div className="opt-detail-stats">
            <div>
              <div className="opt-detail-stat-k">Est profit &amp; loss</div>
              <div className={`opt-detail-stat-v ${pnlTone}`}>
                {targetPnl >= 0 ? "+" : "−"}${formatNum(Math.abs(targetPnl), 2)}
              </div>
            </div>
            <div>
              <div className="opt-detail-stat-k">Est contract price</div>
              <div className="opt-detail-stat-v">
                ${formatNum(premium, 2)}
              </div>
            </div>
            <div>
              <div className="opt-detail-stat-k">Breakeven</div>
              <div className="opt-detail-stat-v">${formatNum(breakeven, 2)}</div>
            </div>
          </div>

          <div className="opt-detail-chart-wrap">
            {/* The target-price callout above the vertical rule — mirrors
                the Robinhood overlay. */}
            <div
              className="opt-detail-target-pill"
              style={{
                left: `${(xAt(target) / W) * 100}%`,
              }}
            >
              <div className={`opt-detail-target-val ${pnlTone}`}>
                ${formatNum(target, 2)} {targetPct >= 0 ? "(+" : "("}{formatNum(targetPct, 2)}%)
              </div>
            </div>

            <svg
              viewBox={`0 0 ${W} ${H}`}
              width="100%"
              height={H}
              preserveAspectRatio="none"
              className="opt-detail-svg"
            >
              {/* Faint vertical gridlines at the strike price. */}
              <line
                x1={xAt(contract.strike)} x2={xAt(contract.strike)}
                y1={PAD_Y} y2={H - PAD_Y}
                stroke="var(--border)" strokeDasharray="2 3"
              />
              {/* Horizontal zero-P&L line — this is the breakeven on the
                  vertical axis; crossing above is profit, below is loss. */}
              <line
                x1={PAD_X} x2={W - PAD_X}
                y1={y0} y2={y0}
                stroke="var(--text)" strokeOpacity={0.5}
                strokeWidth={1}
              />
              {/* Loss region (below zero) in red. */}
              <path d={polyBelow} fill="var(--down, #f04452)" fillOpacity={0.35} />
              {/* Profit region (above zero) in green. */}
              <path d={polyAbove} fill="var(--up, #22c55e)" fillOpacity={0.35} />
              {/* Payoff line itself — bright so the kink reads clearly. */}
              <path d={line} fill="none" stroke={targetPnl >= 0 ? "var(--up)" : "var(--down)"} strokeWidth={2} />

              {/* Target scrubber vertical rule. */}
              <line
                x1={xAt(target)} x2={xAt(target)}
                y1={PAD_Y} y2={H - PAD_Y}
                stroke="var(--text)" strokeOpacity={0.7}
                strokeWidth={1}
              />
              {/* Dot at the scrubbed point on the payoff line. */}
              <circle
                cx={xAt(target)}
                cy={yAt(pnlAt(target))}
                r={6}
                fill={targetPnl >= 0 ? "var(--up)" : "var(--down)"}
                stroke="var(--bg)"
                strokeWidth={2}
              />

              {/* X-axis strike ticks: show 4-5 equally spaced labels. */}
              {axisTicks(lo, hi, 5).map((v) => (
                <text
                  key={v}
                  x={xAt(v)}
                  y={H - 4}
                  fill="var(--muted)"
                  fontSize={11}
                  textAnchor="middle"
                >${formatNum(v, v >= 100 ? 0 : 1)}</text>
              ))}
            </svg>

            <input
              type="range"
              className="opt-detail-slider"
              min={scrubMin}
              max={scrubMax}
              step={0.01}
              value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
              aria-label="Target underlying price"
            />

            {/* Time scrubber: Now → Expiration. Dragging left bows the curve
                up (time value remaining); dragging right collapses it to the
                kink of expiration. Mirrors Robinhood's bottom slider. */}
            <div className="opt-detail-time-row">
              <div className="opt-detail-time-labels">
                <span>Now</span>
                {timeTicks(days).map((tk) => (
                  <span key={tk.label}>{tk.label}</span>
                ))}
                <span>Exp</span>
              </div>
              <input
                type="range"
                className="opt-detail-slider opt-detail-time-slider"
                min={0}
                max={1}
                step={0.001}
                value={timeProgress}
                onChange={(e) => setTimeProgress(Number(e.target.value))}
                aria-label="Time to expiration"
              />
              <div className="opt-detail-time-now mono">
                {formatSimTime(expiry, days, timeProgress)}
              </div>
            </div>

            <div className="opt-detail-meta">
              <span>Spot: ${formatNum(spot, 2)}</span>
              <span>IV: {formatNum(sigma * 100, 1)}%</span>
              <span>Strike: ${formatNum(contract.strike, 2)}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function axisTicks(lo: number, hi: number, n: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(lo + ((hi - lo) * i) / (n - 1));
  return out;
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  // Robinhood-style short form: M/D/YY
  const yr = String(d.getFullYear()).slice(-2);
  return `${d.getMonth() + 1}/${d.getDate()}/${yr}`;
}

/** Milestone labels between Now and Exp. We pick a few evenly spaced
 *  calendar dates on the way to expiration so the time axis is readable
 *  without hover: short-dated contracts get one or two midpoints, long
 *  ones get a couple of month markers. */
function timeTicks(days: number): { label: string; frac: number }[] {
  if (days <= 1) return [];
  const now = Date.now();
  const expMs = now + days * 86400_000;
  const fracs = days < 14 ? [0.5] : days < 60 ? [0.33, 0.66] : [0.25, 0.5, 0.75];
  const fmt = (ms: number) => {
    const d = new Date(ms);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };
  return fracs.map((f) => ({ label: fmt(now + (expMs - now) * f), frac: f }));
}

/** The current scrubbed timestamp + DTE for display next to the time
 *  slider. Reads like Robinhood's "Jan 15 '27, 4:00 PM (3 DTE)". */
function formatSimTime(expiryIso: string, days: number, progress: number): string {
  const expMs = new Date(expiryIso).getTime();
  if (!Number.isFinite(expMs)) return `${days} DTE`;
  const nowMs = expMs - days * 86400_000;
  const atMs = nowMs + (expMs - nowMs) * progress;
  const d = new Date(atMs);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const dteNow = Math.max(0, Math.round((expMs - atMs) / 86400_000));
  return `${date} · ${dteNow} DTE`;
}
